// External libraries
// @ts-expect-error untyped-import
import { isString } from '@checkout/react-components';

// Internal modules
import { conditionallyDropLoginCookie } from '../login';
import { LOG_IN_PAYLOAD, logger } from '@/lib/logger';
import { isDev, logDevErrorToConsole } from '../env';
import { buildIWCRedirectUrl, buildUrl, getRedirectUrl } from '../url';
import { appStore } from '@/store';
import { features } from '@/lib/features';
import { getAccessToken, getGlobals, getLocale, getToken } from '@/lib/globals';
import { goToMemberXO } from './goToMemberXO';
import { logFPTISpinnerDuration } from '@/components/Common/Spinner/fpti';
import type { AppMode, GenericObjectType } from '@/types';
import type { BasicLoggerMethods } from '@/lib/logger/types';
import type { REDIRECT_URLS_MAP } from '../url/constants';
import type { RedirectOptionsType } from './types';
import { isFunction } from '@/lib/utils/typeUtils';
import { CI } from '@/lib/customerInteractions';
import { getParams } from '@/lib/utils/params';
import { atomics, atomicsDomain, atomicsStatus } from '@/lib/atomics';
import { getExperiment } from '@/lib/features/utils';

/**
 * This intercepts member redirects and uses internal slingshot if cookies are
 * disabled and falls back to window location if the internal redirect throws
 * once the main member redirect is refactored this handler can be removed
 * TODO: Refactor function for redirect dumbledore
 * @param {string} url
 */
const handleRedirect = (url: string): void => {
  // We do not need to use slingshot for login redirects
  if (
    !navigator.cookieEnabled &&
    url.includes('/webapps/hermes') &&
    !url.includes('hermesLoginRedirect')
  ) {
    try {
      const params = new URL(url).search;
      goToMemberXO(params);
    } catch (err: any) {
      logger.error('failed_to_redirect_to_memberXO', err);
      window.location.assign(url);
    }
  } else {
    window.location.assign(url);
  }
};

export const flushLogsAndRedirect = ({
  logKey,
  logPayload,
  logMethod,
  url,
}: {
  logKey?: string;
  logPayload?: GenericObjectType;
  logMethod?: keyof typeof logger;
  url: string;
}): void => {
  // new progressive spinner logger
  logFPTISpinnerDuration(features.config);
  const logName = isString(logKey) ? logKey : 'redirect';
  // @ts-expect-error isFunction should verify that logMethod exists
  const method = isFunction(logger[logMethod]) ? logMethod : 'info';
  // @ts-expect-error we should know that method is defined at this point
  logger[method](logName, { ...logPayload, url });

  // Make sure no CIs are left open
  CI.endAll();

  // TODO: can be cleaned up after we confirm that logger.flushThen
  // is flushing all data as well. As of today on VIP page we are not
  // seeing logger.flushThen flush all the logs
  logger.flush();

  // @ts-expect-error flushThen currently returns "unknown"
  return logger.flushThen(() => handleRedirect(url));
};

/**
 * @function redirect
 * @param {object} options
 */
export const redirect = ({
  endpoint,
  logKey,
  logPayload,
  params = {},
  reason = 'reason_missing',
  type = 'info',
}: RedirectOptionsType): void => {
  if (!isString(endpoint)) {
    logger.error('weasley_attempted_redirect_with_invalid_url', {
      url: endpoint,
    });

    logger.metric('GUEST_SIGNUP.REDIRECT.FAILURE', {
      dimensions: {
        outcome: 'redirect failure. Redirecting to generic error page.',
        outcomeReason: 'invalid url',
        reason,
      },
    });

    return redirectToGenericError();
  }

  if (isDev && type !== 'info') {
    return logDevErrorToConsole('PREVENT REDIRECT');
  }

  // if we're falling back to Xoon or Hermes, log the fallback reason explicitly
  const isXoon: boolean = endpoint?.includes(getRedirectUrl('xoon'));
  const isHermes: boolean = endpoint?.includes(getRedirectUrl('hermes'));

  // Checking for the experiment and not if the treatment is active because we want
  // to log data to FPTI for both treatment and control
  const disallowFallbacksExp = getExperiment(
    'FEATURE_DISALLOW_FALLBACK_TO_XOON',
  );
  if (isXoon && disallowFallbacksExp) {
    const isTreatment = features.isActive('disallowFallbacksToXoon');
    logger.fpti('weasley_disallow_xoon_fallback', {
      experiment_name: 'FEATURE_DISALLOW_FALLBACK_TO_XOON',
      reason: `xoon_fallback_${isTreatment ? 'dis' : ''}allowed`,
      state_name: 'billing',
      transition_name: 'weasley_fallback',
    });

    if (isTreatment) {
      return redirectToGenericError();
    }
  }

  if (isXoon || isHermes) {
    // if we're falling back to Xoon/hermes, log the fallback reason explicitly
    const appName = isXoon ? 'xoon' : 'hermes';
    logger.info(`WEASLEY_fallback_to_${appName}-REDIRECT-${reason}`);

    logger.metric('GUEST_SIGNUP.REDIRECT.FALLBACK', {
      dimensions: {
        outcome: `fallback-redirecting to ${appName}`,
        reason,
        target: appName,
      },
    });
  }

  const { country, locale } = getLocale();
  const additionalParams = {
    'country.x': country,
    fromSignupLite: true,
    'locale.x': locale,
    token: getToken(),
    ...params,
  };
  const url = buildUrl(endpoint, { additionalParams });
  const logMethod = ['info', 'warn', 'error'].includes(type) ? type : 'info';

  flushLogsAndRedirect({ logKey, logMethod, logPayload, url });
};

/**
 * @function redirectToGenericError
 * @param {object} options | { message }
 * @describe Redirects to the generic error page
 */
export const redirectToGenericError = ({
  message = 'DEFAULT',
  params = {},
}: { message?: string; params?: GenericObjectType } = {}): void => {
  const { landingIWCData, uri } = getGlobals();
  // @ts-expect-error genericErrorEndpoint is not defined
  const { base, genericErrorEndpoint } = uri;
  const queryParams: GenericObjectType = { ...params, code: btoa(message) };

  if (isDev) {
    return logDevErrorToConsole(`GENERIC ERROR: ${message}`);
  }

  /**
   * Logging additional error data for FPTI and CAL for DMF
   *
   * `error_source` and `calling_client_id` are hardcoded since requests
   * from Weasley are routed through graphqlnodeweb
   * These fields are included in the payload to assist with querying for DMF dashboards
   */
  logger.fpti('GENERIC_ERROR_PAGE_HANDLER', {
    error: {
      calling_client_id: 'weasley',
      error_source: 'graphqlnodeweb',
      ext_error_code: message,
      ext_error_desc: message,
    },
  });

  logger.error('GENERIC_ERROR_PAGE_HANDLER', {
    error: {
      client: 'weasley',
      downstream_app: 'graphqlnodeweb',
      m_err_reason_code: message,
      m_err_reason_desc: message,
    },
  });

  const { cancelUrl } = appStore.getState().api;
  if (cancelUrl || landingIWCData?.cancelUrl) {
    queryParams.cancelLink = cancelUrl || landingIWCData?.cancelUrl;
  }

  logger.metric('GUEST_SIGNUP.REDIRECT.GENERIC_ERROR', {
    dimensions: {
      outcome: 'redirect to generic error',
      outcomeReason: String(message),
    },
  });
  logger.metric('GUEST_SIGNUP.RENDER.GENERIC_ERROR');
  redirect({
    endpoint: `${base}${genericErrorEndpoint}`,
    logKey: `weasley_redirect_to_generic_error_${message}`,
    params: queryParams,
    reason: 'to_generic_error',
  });
};

/**
 * @function redirectToMerchantOnSuccess
 * @param {string} returnUrl The return url sent from the merchant
 */
export const redirectToMerchantOnSuccess = (
  returnUrl?: string | null,
): void => {
  // If the returnUrl is not a string or has no length then we
  // don't have anywhere to send the user
  if (!isString(returnUrl) || !returnUrl) {
    logger.warn('no_return_after_success');
    logger.metric('GUEST_SIGNUP.FLOW_END', {
      dimensions: {
        outcome: 'No return to merchant',
        outcomeReason: 'No valid return url',
      },
    });
    return;
  }

  logger.info('return_to_merchant_after_success');
  logger.metric('GUEST_SIGNUP.FLOW_END', {
    dimensions: {
      outcome: 'returning to merchant',
    },
  });
  logAtomicsTransactionSuccessfulAndLeaveCheckout();
  CI.endAll(CI.Status.Ok);
  flushLogsAndRedirect({ url: returnUrl });
};

/**
 * @function fallback
 * @param {object} options
 * @description triggers a fallback to xoon or hermes
 */
export const fallback = (
  options: {
    additionalParams?: GenericObjectType;
    app?: keyof typeof REDIRECT_URLS_MAP;
    payload?: GenericObjectType;
    reason?: string;
  } = {},
): void | Promise<void> => {
  const { additionalParams, app = 'xoon', payload = {}, reason } = options;

  if (isDev) {
    return logDevErrorToConsole(`FALLBACK: ${reason}`);
  }

  // If we have an access token we need to redirect to Hermes,
  // even if `app` is `'xoon'`, since the user cannot signup again
  const isSignUpToken = getAccessToken().type === 'signup';
  const appName = isSignUpToken ? 'hermes' : app;

  const redirectOptions: RedirectOptionsType = {
    endpoint: getRedirectUrl(appName),
    logKey: `weasley_fallback_to_${appName}`,
    params: {
      ...additionalParams,
      fallback: 1,
      // @ts-expect-error This needs a check for reason not being defined
      reason: btoa(reason),
    },
    payload,
    reason,
    type: 'error',
  };

  return conditionallyDropLoginCookie().then(() => {
    const { isUserLoggedIn } = appStore.getState().user;

    // If we are in a signup flow and the login failed, redirect
    // to the generic error page
    if (!isUserLoggedIn && isSignUpToken) {
      logger.warn('redirect_to_generic_error_due_to_login_failure');
      CI.fci(CI.ErrorReason.Application);
      return redirectToGenericError();
    }

    redirect(redirectOptions);
  });
};

export const redirectToLogIn = ({
  params = {},
  reason,
}: { params?: GenericObjectType; reason?: string } = {}): void => {
  logger.fpti('weasley_redirect_to_log_in', LOG_IN_PAYLOAD());
  redirect({
    endpoint: getRedirectUrl('hermes'),
    logKey: 'weasley_redirect_to_log_in',
    params: { hermesLoginRedirect: true, prompt: 'login', ...params },
    reason: reason || 'to_login',
  });
};

export const redirectToEndpoint = ({
  endpoint,
  params = {},
  reason = 'unknown',
}: {
  endpoint: string;
  params?: GenericObjectType;
  reason?: string;
}): void => {
  logger.info(`redirect_to_endpoint_reason_${reason}`, { endpoint, reason });
  redirect({
    endpoint: endpoint,
    logKey: `redirect_to_endpoint_reason_${reason}`,
    params,
    reason: reason,
  });
};

/**
 * @function redirectToXoon
 * @param {object} options? (All options are optional)
 * @description This function is used for non-fallback redirects to xoon.
 * An example of such a use case would be when the country is changed to a
 * non-lite supported country.
 */
export const redirectToXoon = ({
  params = {},
  type,
  reason,
}: {
  params?: GenericObjectType;
  type?: BasicLoggerMethods;
  reason?: string;
}): void => {
  const { isBasl } = getGlobals();

  const additionalParams = isBasl
    ? { abTestThrottle: 'xoon', ...params }
    : params;

  redirect({
    endpoint: getRedirectUrl('xoon'),
    logKey: 'weasley_redirect_to_xoon',
    params: additionalParams,
    reason,
    type,
  });
};

/**
 * @function redirectToMember
 * @param {object} params
 * @description This function redirects to member checkout with slingshot
 */
export const redirectToMember = (
  params: GenericObjectType = {},
  type?: BasicLoggerMethods,
): void => {
  redirect({
    endpoint: getRedirectUrl('hermes'),
    logKey: 'redirect_to_memberXO',
    params: {
      ...params,
    },
    reason: 'to_memberXO',
    type,
  });
};

/**
 * @function redirectToConsumer
 * @param {string} flowId
 * @param {object} params
 * @description This function is used for redirects to Consumer app.
 * An example of such a use case would be for the add bank case in JP
 */
export const redirectToConsumer = (
  flowId: string,
  params: GenericObjectType = {},
  logPayload: GenericObjectType = {},
  type?: BasicLoggerMethods,
): void => {
  const flowValue = flowId.replace(/^\?flow=/, '');

  redirect({
    endpoint: getRedirectUrl('tesla', { flow: flowValue }),
    logKey: 'weasley_redirect_to_tesla',
    logPayload,
    params,
    reason: 'to_consumer_tesla',
    type,
  });
};

/**
 * @function redirectToForgotEmail
 * @param {object} params
 * @description This function is used for redirects to forgot email page
 * User can click on "forgot Email" on IWC component on /landing page
 */
export const redirectToForgotEmail = (
  params: GenericObjectType = {},
  type?: BasicLoggerMethods,
): void => {
  const {
    user: { ecToken },
  } = getGlobals();

  const { country, locale } = getLocale();

  redirect({
    endpoint: getRedirectUrl('forgotEmail'),
    logKey: 'weasley_redirect_to_forgot_email',
    params: {
      contextId: ecToken,
      'country.x': country,
      'locale.x': locale,
      redirectUri: buildUrl(getRedirectUrl('dumbledore'), {
        withCurrentParams: true,
      }),
      ...params,
    },
    reason: 'to_forgot_email',
    type,
  });
};

/**
 * @function redirectToPasswordReset
 * @param {object} params Extra parameters to add in the redirect
 * @param {BasicLoggerMethods} type Optional, type of log
 * @description This function is used for redirects to password reset page
 * An example of such a use case would be for POMA users trying to login
 */
export const redirectToPasswordReset = (
  params: GenericObjectType = {},
  type?: BasicLoggerMethods,
): void => {
  const {
    user: { ecToken },
  } = getGlobals();
  const { country, locale } = getLocale();
  const { redirectUri, ...rest } = params;

  // If params has the redirectUri, set the redirectParams as params,
  // else, build the redirectUri and set the params without the redirectUri.
  const redirectParams = redirectUri
    ? { ...params, contextId: ecToken }
    : {
        ...rest,
        contextId: ecToken,
        'country.x': country,
        'locale.x': locale,
        redirectUri: buildIWCRedirectUrl(),
      };

  redirect({
    endpoint: getRedirectUrl('pwRest'),
    logKey: 'weasley_redirect_to_password_reset',
    params: redirectParams,
    reason: 'to_password_reset',
    type,
  });
};

/**
 * @function redirectToBilling
 * @param {string} mode - either guest or signup
 * Redirect to `billingnodeweb` to activate the subscription
 */
export const redirectToBilling = (mode: AppMode): void => {
  const sdkMeta = getParams().sdkMeta ?? window?.xprops?.sdkMeta;
  const mfid = getParams().mfid;
  const endpoint = getRedirectUrl('billing');
  const token = getToken();
  const { country, locale } = getLocale();
  const params = {
    'country.x': country,
    'locale.x': locale,
    mode,
    token,
    ...(sdkMeta ? { sdkMeta } : {}),
    // Read mfid from params in case buyer landed with 302 URL redirection
    // This param is more relevant to WPS subscriptions
    ...(mfid ? { mfid } : {}),
  };
  redirect({
    endpoint,
    logKey: 'weasley_redirect_to_billing',
    params,
    reason: 'to_billing',
    type: 'info',
  });
};

/**
 * @function fallbackToHermesFIContingency
 * @param {object} params | { message }
 * @description This function is used to fallback to hermes
 * contingency on ba without purchase card contigency
 */
export const fallbackToHermesFIContingency = ({
  message,
}: {
  message: string;
}): void => {
  // ported over from xoon
  const addFIContingency = 'noretry';
  const redirectToHermes = true;
  const rcache = 1;

  logger.fpti('weasley_redirect_to_hermes', {
    reason: 'signup_fi_contingency',
    state_name: 'billing',
    transition_name: 'redirect_to_member_flow',
  });

  fallback({
    additionalParams: {
      addFIContingency,
      rcache,
      redirectToHermes,
    },
    app: 'hermes',
    reason: message,
  });
};

/**
 * Complete CTA button click metric upon a successful transaction and redirect back to merchant
 */
export const logAtomicsTransactionSuccessfulAndLeaveCheckout = (): void => {
  const atomicEventState = atomics.getWaitingState();
  if (atomicEventState) {
    atomics.leaveFlow(atomicEventState.viewName, atomicsStatus.ok, {
      domain: atomicsDomain,
      task: atomicEventState.task,
    });
  }
};

/**
 * Redirects to Open Banking flow via MoneyNodeWeb
 * @param fundingInstrumentId - The funding instrument ID (bank ID) for the OB flow
 */
export const redirectToOpenBanking = (fundingInstrumentId: string): void => {
  const token = getToken();

  logger.info('weasley_redirect_to_open_banking', {
    fundingInstrumentId,
  });

  logger.fpti('OPEN_BANKING_FLOW_INITIATED', {
    funding_instrument_id: fundingInstrumentId,
    state_name: 'open_banking',
    transition_name: 'open_banking_flow_initiated',
  });

  logger.metric('GUEST_SIGNUP.OPEN_BANKING.REDIRECT_TO_MNW', {
    dimensions: {
      has_funding_instrument_id: true,
    },
  });

  const { form: formState } = appStore.getState();
  const onboardOption = formState.onboardOption?.value || 'guest';

  const openBankingUrl = buildUrl('/checkoutweb/openBanking/', {
    additionalParams: {
      id: fundingInstrumentId,
      onboardOption,
      token,
    },
    withCurrentParams: true,
  });

  flushLogsAndRedirect({
    logKey: 'REDIRECT_TO_OPEN_BANKING',
    logPayload: {
      fundingInstrumentId,
      url: openBankingUrl,
    },
    url: openBankingUrl,
  });
};

/**
 * Given a stepUpContext, builds the URL to redirect to the auth challenge app.
 * If the user needs additional confirmation before being allowed to complete the transaction.
 * @param {string} stepupContext - passed to authchallengenodeweb like `VERIFY_USER_IDENTITY`
 * */
export function redirectToAuthChallenge(
  stepupContext: string,
  extraParams = {},
): void {
  const currentParams = getParams();

  // Check if we've already redirected by looking for atoSuccess param in URL
  // This param is added when redirecting to auth challenge and persists on return
  // If we've already been to auth challenge and returned, redirect to generic error
  // to prevent infinite redirect loops and inform the user of the issue
  if (currentParams.atoSuccess === 'true') {
    logger.warn('WEASLEY_REDIRECT_TO_AUTHCHALLENGE_ALREADY_REDIRECTED', {
      atoSuccess: currentParams.atoSuccess,
      reason: 'auth_challenge_completed_but_verification_still_failing',
      stepupContext,
    });
    return redirectToGenericError({
      message: 'AUTH_CHALLENGE_VERIFICATION_FAILED',
    });
  }

  // End current interactions - using Weasley's CI module
  CI.endAll(CI.Status.Error, CI.ErrorReason.Auth);

  const { country, locale } = getLocale();
  const accessToken = getAccessToken();
  const globals = getGlobals();

  const queryParams: Record<string, unknown> = {
    atoSuccess: 'true',
    ...currentParams,
    ...extraParams,
    invalidateCheckoutSessionPlan: true, // We need to force replan on return to lite
    lite: 1,
  };

  delete queryParams.returnToLiteUri;

  // Use /checkoutweb path for Weasley (always return to lite)
  const path = '/checkoutweb';
  const returnUri = buildUrl(path, {
    additionalParams: queryParams,
    withCurrentParams: false,
  });

  const { uri } = globals;

  // Cast uri to access auth endpoints that exist at runtime but aren't in types
  const uriWithAuthEndpoints = uri as any;

  // For Weasley (accountless users), we should use direct authnodeweb redirect
  // without trying to pass access tokens since accountless users don't have them
  const authEndpoint =
    (uriWithAuthEndpoints?.authNodeWebEndpoint ||
      uriWithAuthEndpoints?.authchallengeEndpoint) ??
    '';

  // Build authnodeweb URL with required parameters for accountless users
  const authChallengeUrl = buildUrl(authEndpoint, {
    additionalParams: {
      returnUri,
      stepupContext,
    },
  });

  flushLogsAndRedirect({
    logKey: 'REDIRECT_TO_AUTHCHALLENGE',
    logPayload: {
      accessTokenPresent: !!accessToken.value,
      accessTokenType: accessToken.type,
      authEndpoint,
      country,
      locale,
      returnUri,
      stepupContext,
      url: authChallengeUrl,
    },
    url: authChallengeUrl,
  });
}
